import { clientConfigs } from './../configs/client-config';
import { QuoteFormConfigDTO } from '../DTO/QuoteFormConfigDTO';
import { dispatchCustomEvent, getUrlVars } from '../helpers';
import BackEndService from './back-end.service';
import { quoteFormInit } from './quote-form';
import * as style from '../assets/style.css';
import { environment } from '../environments/environment';
import { FieldDTO } from '../DTO/FieldDTO';
import { version } from './../../package.json';

declare global {
    interface Window {
        sosQuoteFunctions: any;
        sos_forms_config: any;
    }
}

export class QuoteFormController {

    public instanceFunctions: { [key: string]: any } = {};

    private buttonsSelector =
        '[data-request-quote-btn], [data-request-quote-btn] *, a[href$="#open-request-quote-form"] *, a[href$="#open-request-quote-form"]';
    private sideContainer = null;
    private popupContainer = null;

    private sideFormContainer = null;
    private popupFormContainer = null;

    private backEndService: BackEndService = null;

    private version = '/api/v2';

    private defaultConfig: QuoteFormConfigDTO = null;

    private configs: { [key: string]: QuoteFormConfigDTO } = {};

    constructor(currentScript) {

        console.debug('QuoteForm version - ' + version);

        style;
        const scriptUrl = new URL(currentScript.src);
        const queryParams = getUrlVars(scriptUrl.search);

        // Append instance functions to window
        this.instanceFunctions.openForm = (template = null) => {
            return this.openForm(template);
        };
        window.sosQuoteFunctions = this.instanceFunctions;

        if (!queryParams.token) {
            throw new Error('Missing search param token');
        }

        this.fetchConfiguration(queryParams.template);

        const apiConfig = {
            // api_url: scriptUrl.origin,
            token: queryParams.token,
        };

        // ! turn on for production
        this.defaultConfig = {
            ...this.defaultConfig,
            ...apiConfig,
        };

        dispatchCustomEvent('serviceos-forms-loaded', {});

        this.init();
    }

    public init() {
        return new Promise(async (res, rej) => {
            try {
                await this.appendInvisibleRecaptchaScript();
                this.renderTheSideForm();
                this.renderThePopupForm();
                this.appendListeners();

                this.checkForAutoOpenForms();
            } catch (e) {
                // console.log(e);
            }
        });
    }

    private appendListeners() {
        document.body.addEventListener('click', (e: any) => {
            let element;
            const target = e.target;

            element = this.checkElementMatchSelector(
                target,
                this.buttonsSelector
            );

            if (element) {
                e.preventDefault();
                const updatedConfig = this.setPreFilledFields(target);
                dispatchCustomEvent('serviceos-forms-open', {
                    trigger: target,
                    source_abbr: updatedConfig.source_abbr,
                });
                this.showForm(updatedConfig);
            } else {
                const quoteCloseClick = this.checkElementMatchSelector(
                    target,
                    '.request-quote-form-container *.close-quote-form, .request-quote-popup-form-modal *.close-quote-form'
                );
                if (quoteCloseClick) {
                    const container = target.closest(
                        '.request-quote-form-container, .request-quote-popup-form-modal'
                    );
                    this.hideForm(container);
                }
            }
        });
    }

    private openForm(template) {
        
        this.fetchConfiguration(template);

        const updatedConfig = this.setPreFilledFields(null);

        const openDelay = window?.sos_forms_config?.delay || null;

        setTimeout(() => {
            dispatchCustomEvent('serviceos-forms-open', {
                trigger: 'custom open',
                source_abbr: updatedConfig.source_abbr,
            });
            
            this.showForm(updatedConfig);
        }, openDelay);
    }

    private setPreFilledFields(element?) {
        let queryParams: any = {};

        let config: QuoteFormConfigDTO = JSON.parse(
            JSON.stringify(this.defaultConfig)
        );

        config.validation = this.defaultConfig.validation;

        if (element) {
            let button: any = this.findButton(element, 0);

            if (button.href) {
                queryParams = getUrlVars(new URL(button.href).search);
            }

            // Change the config base on trigger button.
            if (
                button.getAttribute('data-request-quote-btn') &&
                button.getAttribute('data-request-quote-template')
            ) {
                const template = button.getAttribute('data-request-quote-template');
                if (this.configs[template]) {
                    config = JSON.parse(JSON.stringify(this.configs[template]));
                    config.validation = this.configs[template].validation;
                }
            } else if (button.href && queryParams['template']) {
                const template = queryParams['template'];
                if (this.configs[template]) {
                    config = JSON.parse(JSON.stringify(this.configs[template]));
                    config.validation = this.configs[template].validation;
                }
            }

            // set source_abbr
            if (
                button.getAttribute('data-request-quote-btn') &&
                button.getAttribute('data-request-quote-source_abbr')
            ) {
                config.source_abbr = button.getAttribute(
                    'data-request-quote-source_abbr'
                );
            } else if (button.href && queryParams['source_abbr']) {
                config.source_abbr = queryParams['source_abbr'];
            }

            config.layout.fields.map((field: FieldDTO) => {
                if (
                    button.getAttribute('data-request-quote-btn') &&
                    button.getAttribute('data-request-quote-' + field.name)
                ) {
                    field.default_value = button.getAttribute(
                        'data-request-quote-' + field.name
                    );
                } else if (button.href && queryParams[field.name]) {
                    field.default_value = queryParams[field.name];
                }
                return field;
            });
        }

        return config;
    }

    private findButton(target: any, index: number): any {
        index += 1;

        if (index == 11) {
            // search only to 10 parent element in dom
            return null;
        }

        if (
            target.getAttribute('data-request-quote-btn') ||
            (target.href && target.href.includes('#open-request-quote-form'))
        ) {
            return target;
        } else {
            return this.findButton(target.parentElement, index);
        }
    }

    private checkElementMatchSelector(element, selector) {
        if (typeof element.matches !== 'undefined') {
            element = element.matches(selector);
        } else if (typeof element.matchesSelector !== 'undefined') {
            element = element.matchesSelector(selector);
        } else if (typeof element.msMatchesSelector !== 'undefined') {
            element = element.msMatchesSelector(selector);
        } else if (typeof element.webkitMatchesSelector !== 'undefined') {
            element = element.webkitMatchesSelector(selector);
        }

        return element;
    }

    private renderTheSideForm() {
        this.sideContainer = document.createElement('div');

        this.sideContainer.innerHTML =
            "<div style='display:flex; justify-content: flex-end;'><span class='close-quote-form icon-close'></span></div>";

        const backgroundColor = this.defaultConfig.layout.theme.background;
        const accentColor = this.defaultConfig.layout.theme.accent;
        const text = this.defaultConfig.layout.theme.text;

        this.sideContainer.classList.add('request-quote-form-container');
        this.sideContainer.style = `background-color: ${backgroundColor} !important; border-left: 1px solid ${accentColor} !important; color: ${text} !important;`;

        this.sideFormContainer = document.createElement('div');

        this.sideContainer.appendChild(this.sideFormContainer);

        document.body.appendChild(this.sideContainer);
    }

    private renderThePopupForm() {
        const quoteContainer: any = document.createElement('div');

        quoteContainer.innerHTML =
            "<div style='display:flex; justify-content: flex-end;'><span class='close-quote-form icon-close'></span></div>";

        const backgroundColor = this.defaultConfig.layout.theme.background;
        const accentColor = this.defaultConfig.layout.theme.accent;
        const text = this.defaultConfig.layout.theme.text;

        quoteContainer.classList.add('request-quote-popup-form-container');
        quoteContainer.style = `background-color: ${backgroundColor} !important; border: 1px solid ${accentColor} !important; color: ${text} !important;`;

        this.popupFormContainer = document.createElement('div');

        quoteContainer.appendChild(this.popupFormContainer);

        this.popupContainer = document.createElement('div');
        this.popupContainer.classList.add('request-quote-popup-form-modal');
        // this.popupContainer.classList.add('close-quote-form');
        this.popupContainer.appendChild(quoteContainer);

        document.body.appendChild(this.popupContainer);
    }

    showForm(config: QuoteFormConfigDTO) {

        if (config?.show_once_session) {
            const key = `sos-quote-form-${config.id}-${config.layout.type}`;
            try {
                if (sessionStorage.getItem(key)) {
                    return;
                } else {
                    sessionStorage.setItem(key, 'true');
                }
            } catch (e) {}
        }

        if (config?.layout?.type === 'popup') {
            this.backEndService = new BackEndService(
                this.defaultConfig.api_url + this.version,
                this.defaultConfig.token
            );
            
            quoteFormInit(this.popupFormContainer, config, this.backEndService);
            
            this.popupContainer.serviceosFormConfig = config;
            this.popupContainer.classList.add('active');
            return;
        }

        this.backEndService = new BackEndService(
            this.defaultConfig.api_url + this.version,
            this.defaultConfig.token
        );
        
        quoteFormInit(this.sideFormContainer, config, this.backEndService);
        this.sideContainer.serviceosFormConfig = config;
        this.sideContainer.classList.add('active');
    }

    hideForm(targetContainer) {
        if (targetContainer === this.sideContainer) {
            dispatchCustomEvent('serviceos-forms-close', {
                source_abbr: this.sideContainer.serviceosFormConfig.source_abbr,
            });
            this.sideContainer.classList.remove('active');
        } else {
            dispatchCustomEvent('serviceos-forms-close', {
                source_abbr:
                    this.popupContainer.serviceosFormConfig.source_abbr,
            });
            this.popupContainer.classList.remove('active');
        }
    }

    fetchConfiguration(template) {
        this.configs = clientConfigs;
        this.configs = this.applyFormsOverwrite(this.configs);
        this.defaultConfig = clientConfigs.default;
        if (clientConfigs[template]) {
            this.defaultConfig = clientConfigs[template];
        }
    }

    applyFormsOverwrite(config) {
        const formOverwriteConfig = window.sos_forms_config;
        if (!formOverwriteConfig) {
            return config;
        }
        const overwriteForms = Object.keys(formOverwriteConfig);

        Object.keys(config).map((key) => {
            if (key == 'default') {
                return config;
            }

            let formConfig = config[key];
            if (overwriteForms.includes(key)) {
                let overwriteConfig =
                    formOverwriteConfig[key];

                const allowsPropsChange = [ 'type', 'description', 'title', 'footer_text', 'submit_text', 'button_text' ];

                allowsPropsChange.forEach((prop) => {
                    if (overwriteConfig[prop] != undefined && typeof overwriteConfig[prop] == 'string') {
                        formConfig.layout[prop] = overwriteConfig[prop];
                    }
                });
            }
        });

        return config;
    }

    checkForAutoOpenForms() {
        window.addEventListener('load', () => {
            const selector =
                '[data-request-quote-btn], a[href$="#open-request-quote-form"]';
            const allFormsTriggers = document.querySelectorAll(selector);

            for (let index = 0; index < allFormsTriggers.length; index++) {
                const formTrigger: any = allFormsTriggers[index];
                let queryParams: any = {};

                if (formTrigger.href) {
                    queryParams = getUrlVars(new URL(formTrigger.href).search);
                }

                if (
                    formTrigger.getAttribute('data-request-quote-btn') &&
                    formTrigger.getAttribute('data-request-quote-auto_open')
                ) {
                    const delay =
                        parseInt(
                            formTrigger.getAttribute('data-request-quote-delay')
                        ) || 0;
                    setTimeout(() => {
                        const updatedConfig =
                            this.setPreFilledFields(formTrigger);
                        dispatchCustomEvent('serviceos-forms-open', {
                            trigger: formTrigger,
                            source_abbr: updatedConfig.source_abbr,
                        });
                        this.showForm(updatedConfig);
                    }, delay);
                } else if (formTrigger.href && queryParams['auto_open']) {
                    const delay = parseInt(queryParams['delay']) || 0;
                    setTimeout(() => {
                        const updatedConfig =
                            this.setPreFilledFields(formTrigger);
                        dispatchCustomEvent('serviceos-forms-open', {
                            trigger: formTrigger,
                            source_abbr: updatedConfig.source_abbr,
                        });
                        this.showForm(updatedConfig);
                    }, delay);
                }
            }
        });
    }

    private appendInvisibleRecaptchaScript() {
        return new Promise((res) => {
            const recaptchaScript = document.createElement('script');
            recaptchaScript.src =
                this.defaultConfig.captcha.settings.google.script +
                '?onload=gcaptchaLoadCallback&render=explicit';
            document.body.appendChild(recaptchaScript);

            window['gcaptchaLoadCallback'] = () => {
                // console.log('captcha');
                res(true);
            };

            recaptchaScript.onerror = () => {
                this.defaultConfig.captcha.type = 'js-captcha';
                res(false);
            };
        });
    }
}
